import {defineMessages} from "@project/i18n";

export const offerEditorWorkspaceMessages = defineMessages({
  editor: {
    id: "sales.offers.editor.workspace.editor",
    defaultMessage: "Editor",
    description: "Standalone editor breadcrumb after the actual Resource ancestor",
  },
  implicitQuantity: {
    id: "sales.offers.editor.workspace.implicitQuantity",
    defaultMessage: "Not specified (uses 1)",
    description: "Omitted authored quantity is valid and has effective quantity one",
  },
  commissionApplied: {
    id: "sales.offers.editor.workspace.commissionApplied",
    defaultMessage: "Commission applied",
    description: "Authored commission applicability in the editor outline",
  },
  commissionExempt: {
    id: "sales.offers.editor.workspace.commissionExempt",
    defaultMessage: "Commission exempt",
    description: "Authored chapter commission exemption in the editor outline",
  },
  coefficientReview: {
    id: "sales.offers.editor.workspace.coefficientReview",
    defaultMessage: "Coefficient review",
    description: "Customer lifecycle pricing stage",
  },
  readyToSend: {
    id: "sales.offers.editor.workspace.readyToSend",
    defaultMessage: "Ready to send",
    description: "Approved Customer stage remains live until Send",
  },
  structure: {
    id: "sales.offers.editor.workspace.structure",
    defaultMessage: "Offer structure",
    description: "Canonical authored tree grid landmark",
  },
  preview: {
    id: "sales.offers.editor.workspace.preview",
    defaultMessage: "Preview",
    description: "Unsaved authored preview heading",
  },
  search: {
    id: "sales.offers.editor.workspace.search",
    defaultMessage: "Search offer structure",
    description: "Local authored tree search",
  },
  chapter: {
    id: "sales.offers.editor.workspace.chapter",
    defaultMessage: "Chapter",
    description: "Chapter node type and empty-title display",
  },
  richText: {
    id: "sales.offers.editor.workspace.richText",
    defaultMessage: "Rich text",
    description: "Rich-text node type and modal heading",
  },
  catalog: {
    id: "sales.offers.editor.workspace.catalog",
    defaultMessage: "Catalog item",
    description: "Sold catalog node type and modal heading",
  },
  header: {
    id: "sales.offers.editor.workspace.header",
    defaultMessage: "Header",
    description: "Required header rich-text scope",
  },
  footer: {
    id: "sales.offers.editor.workspace.footer",
    defaultMessage: "Footer",
    description: "Required footer rich-text scope",
  },
  addChapter: {
    id: "sales.offers.editor.workspace.addChapter",
    defaultMessage: "Add chapter",
    description: "Local chapter creation action",
  },
  addRichText: {
    id: "sales.offers.editor.workspace.addRichText",
    defaultMessage: "Add rich text",
    description: "Local rich-text block creation action",
  },
  addCatalog: {
    id: "sales.offers.editor.workspace.addCatalog",
    defaultMessage: "Add catalog item",
    description: "Local sold-item block creation action",
  },
  edit: {
    id: "sales.offers.editor.workspace.edit",
    defaultMessage: "Edit",
    description: "Open scoped local node editor",
  },
  clear: {
    id: "sales.offers.editor.workspace.clear",
    defaultMessage: "Clear",
    description: "Clear required singleton rich text and its variable declarations",
  },
  remove: {
    id: "sales.offers.editor.workspace.remove",
    defaultMessage: "Remove",
    description: "Remove local authored node",
  },
  up: {
    id: "sales.offers.editor.workspace.up",
    defaultMessage: "Move up",
    description: "Move local node before preceding sibling",
  },
  down: {
    id: "sales.offers.editor.workspace.down",
    defaultMessage: "Move down",
    description: "Move local node after following sibling",
  },
  visualize: {
    id: "sales.offers.editor.workspace.visualize",
    defaultMessage: "Show in preview",
    description: "Focus native preview node for scoped outline target",
  },
  actions: {
    id: "sales.offers.editor.workspace.actions",
    defaultMessage: "Actions",
    description: "Scoped outline row action menu",
  },
  expand: {
    id: "sales.offers.editor.workspace.expand",
    defaultMessage: "Expand all",
    description: "Expand authored chapter nodes",
  },
  collapse: {
    id: "sales.offers.editor.workspace.collapse",
    defaultMessage: "Collapse all",
    description: "Collapse authored chapter nodes",
  },
  variables: {
    id: "sales.offers.editor.workspace.variables",
    defaultMessage: "Variables",
    description: "Global variable inventory drawer with scoped usages",
  },
  variableSearch: {
    id: "sales.offers.editor.workspace.variableSearch",
    defaultMessage: "Search variables",
    description: "Local global-variable name search",
  },
  globalVariableHelp: {
    id: "sales.offers.editor.workspace.variables.globalHelp",
    defaultMessage: "Apply a resolution to every occurrence of the same variable name.",
    description:
      "Explain explicit global variable resolution without merging stored values on read",
  },
  mixedValues: {
    id: "sales.offers.editor.workspace.variables.mixed",
    defaultMessage: "Mixed values",
    description: "Existing same-name declarations differ in value or remembered state",
  },
  unresolved: {
    id: "sales.offers.editor.workspace.unresolved",
    defaultMessage: "Unresolved",
    description: "Explicit null authored input",
  },
  literal: {
    id: "sales.offers.editor.workspace.literal",
    defaultMessage: "Literal",
    description: "Literal variable value kind without publishing its raw value",
  },
  binding: {
    id: "sales.offers.editor.workspace.binding",
    defaultMessage: "Business binding",
    description: "Finite binding reference kind",
  },
  stale: {
    id: "sales.offers.editor.workspace.stale",
    defaultMessage: "This editor target changed. Open the current target before applying changes.",
    description: "Withdraw obsolete local editing workflow",
  },
  name: {
    id: "sales.offers.editor.workspace.name",
    defaultMessage: "Name",
    description: "Authored outline name column",
  },
  type: {
    id: "sales.offers.editor.workspace.type",
    defaultMessage: "Type",
    description: "Authored outline node kind column",
  },
  summary: {
    id: "sales.offers.editor.workspace.summary",
    defaultMessage: "Input",
    description: "Authored input summary, not current commercial pricing",
  },
  savedIssues: {
    id: "sales.offers.editor.workspace.savedIssues",
    defaultMessage: "Saved evaluation issues",
    description: "Authoritative saved issues, not a recomputation for unsaved content",
  },
  status: {
    id: "sales.offers.editor.workspace.status",
    defaultMessage: "Status",
    description: "Authoring/evaluation row status",
  },
  price: {
    id: "sales.offers.editor.workspace.price",
    defaultMessage: "Price",
    description: "Readonly selling price or supplier cost",
  },
  priceDetails: {
    id: "sales.offers.editor.workspace.priceDetails",
    defaultMessage: "Price details",
    description: "Readonly backend pricing detail preview",
  },
  salePrice: {
    id: "sales.offers.editor.workspace.salePrice",
    defaultMessage: "Sale price",
    description: "Admitted chapter selling Money",
  },
  notPriced: {
    id: "sales.offers.editor.workspace.notPriced",
    defaultMessage: "Not priced",
    description: "Coefficient-dependent fact has not yet been priced",
  },
  unitSupplierCost: {
    id: "sales.offers.editor.workspace.unitSupplierCost",
    defaultMessage: "Unit supplier cost",
    description: "Exact native supplier cost for one sold unit",
  },
  calculatedTotal: {
    id: "sales.offers.editor.workspace.calculatedTotal",
    defaultMessage: "Calculated total",
    description: "Chapter total before commercial rounding",
  },
  supplierCost: {
    id: "sales.offers.editor.workspace.supplierCost",
    defaultMessage: "Supplier cost",
    description: "Exact backend extended cost, not a frontend sum",
  },
  setCommercialOptions: {
    id: "sales.offers.editor.workspace.setCommercialOptions",
    defaultMessage: "Chapter options",
    description: "Edit optionality and commission applicability during pricing review",
  },
  setCoefficient: {
    id: "sales.offers.editor.workspace.setCoefficient",
    defaultMessage: "Set coefficient",
    description: "Open the editor's chapter coefficient decision form",
  },
  coefficient: {
    id: "sales.offers.editor.workspace.coefficient",
    defaultMessage: "Coefficient",
    description: "Backend admitted pricing coefficient",
  },
  commissionFraction: {
    id: "sales.offers.editor.workspace.commissionFraction",
    defaultMessage: "Commission",
    description: "Backend admitted commission fraction formatted as percentage",
  },
  chapterSum: {
    id: "sales.offers.editor.workspace.chapterSum",
    defaultMessage: "Chapter sum",
    description: "Backend admitted chapter cost Money",
  },
  costWithBenefit: {
    id: "sales.offers.editor.workspace.costWithBenefit",
    defaultMessage: "Cost with benefit",
    description: "Backend admitted cost with benefit Money",
  },
  commissionAmount: {
    id: "sales.offers.editor.workspace.commissionAmount",
    defaultMessage: "Commission amount",
    description: "Backend admitted commission Money",
  },
  issues: {
    id: "sales.offers.editor.workspace.issues",
    defaultMessage: "Needs input",
    description: "Unresolved authored input or a current scoped backend issue",
  },
  protected: {
    id: "sales.offers.editor.workspace.protected",
    defaultMessage: "Protected",
    description: "Backend protection metadata, independent of issue status",
  },
  evaluated: {
    id: "sales.offers.editor.workspace.evaluated",
    defaultMessage: "Evaluated",
    description: "Current admitted evaluation with no unresolved row input",
  },
  unevaluated: {
    id: "sales.offers.editor.workspace.unevaluated",
    defaultMessage: "Not evaluated",
    description: "No current admitted evaluation for the working draft",
  },
  filter: {
    id: "sales.offers.editor.workspace.filter",
    defaultMessage: "Filter structure",
    description: "Local outline status filter",
  },
  allRows: {
    id: "sales.offers.editor.workspace.allRows",
    defaultMessage: "All rows",
    description: "All authored outline rows, not Resource lifecycle state",
  },
  noIssues: {
    id: "sales.offers.editor.workspace.noIssues",
    defaultMessage: "No saved issues.",
    description: "No issues in the owner's saved evaluation snapshot",
  },
  configured: {
    id: "sales.offers.editor.workspace.configured",
    defaultMessage: "Configured",
    description: "Singleton content has authored text or declarations",
  },
  emptyContent: {
    id: "sales.offers.editor.workspace.emptyContent",
    defaultMessage: "Empty",
    description: "Singleton content is empty",
  },
  financials: {
    id: "sales.offers.editor.workspace.financials",
    defaultMessage: "Financials",
    description: "Customer-only admitted financial summary",
  },
  save: {
    id: "sales.offers.editor.workspace.save",
    defaultMessage: "Save",
    description: "Resource-owner save-state card",
  },
  total: {
    id: "sales.offers.editor.workspace.total",
    defaultMessage: "Total",
    description: "Backend selling total",
  },
  benefit: {
    id: "sales.offers.editor.workspace.benefit",
    defaultMessage: "Benefit",
    description: "Backend benefit and percentage",
  },
  undefinedPercentage: {
    id: "sales.offers.editor.workspace.undefinedPercentage",
    defaultMessage: "Undefined percentage",
    description: "Available zero selling total has no benefit percentage",
  },
  evaluationUnavailable: {
    id: "sales.offers.editor.workspace.evaluationUnavailable",
    defaultMessage: "Current financial evaluation unavailable.",
    description: "No matching admitted evaluation is supplied for the displayed draft",
  },
  placeholder: {
    id: "sales.offers.editor.workspace.placeholder",
    defaultMessage: "Write content…",
    description: "Local rich-text field placeholder",
  },
});
